import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Gauge,
  PackageCheck,
  Boxes,
  Gift,
  History,
  Wallet,
  Users,
  LogOut,
  Menu,
  Truck,
  Clock,
  Timer,
  CalendarX,
  MapPin,
  DollarSign,
  X,
  Trophy,
  Receipt,
  Settings,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canAccessSection, roleLabel, isGestor } from "@/lib/permissions";
import { ReportLogo } from "@/components/ReportLogo";

const REPORT_PATHS = ["/folha", "/custo-operacional", "/beneficios", "/despesas"];

function SettingsLink({ testId, size }) {
  const { user } = useAuth();
  if (!isGestor(user)) return null;
  return (
    <NavLink to="/configuracoes" data-testid={testId} title="Configurações"
      className={({ isActive }) => `rounded-md p-2 transition-colors hover:bg-slate-800 hover:text-orange-300 ${isActive ? "text-orange-400" : "text-slate-400"}`}>
      <Settings className={size} />
    </NavLink>
  );
}
import { useBase } from "@/context/BaseContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

function BaseSelector({ testId, className }) {
  const { bases, selectedBaseId, setSelectedBaseId } = useBase();
  return (
    <Select value={selectedBaseId || "all"} onValueChange={(v) => setSelectedBaseId(v === "all" ? "" : v)}>
      <SelectTrigger data-testid={testId} className={`h-9 border-slate-700 bg-slate-900 text-slate-100 ${className || "w-[190px]"}`}>
        <div className="flex items-center gap-1.5 truncate"><MapPin className="h-3.5 w-3.5 shrink-0 text-orange-400" /><SelectValue /></div>
      </SelectTrigger>
      <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
        <SelectItem value="all">Todas as bases</SelectItem>
        {bases.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`base-opt-${b.id}`}>{b.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const NAV = [
  { to: "/dashboard", label: "Visão Geral", icon: LayoutDashboard },
  { to: "/auditoria-km", label: "Auditoria de KM", icon: Gauge },
  { to: "/auditoria-entregas", label: "Auditoria de Entregas", icon: PackageCheck },
  { to: "/ponto", label: "Registro de Ponto", icon: Clock },
  { to: "/contador-horas", label: "Contador de Horas", icon: Timer },
  { to: "/faltas", label: "Faltas", icon: CalendarX },
  { to: "/estoque", label: "Estoque", icon: Boxes },
  { to: "/bases", label: "Bases", icon: MapPin },
  { to: "/beneficios", label: "Benefícios", icon: Gift },
  { to: "/historico", label: "Histórico", icon: History },
  { to: "/folha", label: "Folha de Pagamentos", icon: Wallet },
  { to: "/custo-operacional", label: "Custo Operacional", icon: DollarSign },
  { to: "/colaboradores", label: "Colaboradores", icon: Users },
  { to: "/ranking", label: "Ranking", icon: Trophy },
  { to: "/despesas", label: "Despesas", icon: Receipt },
];

function NavItems({ onNavigate }) {
  const { user } = useAuth();
  const items = NAV.filter((item) => canAccessSection(user, item.to));
  return (
    <nav className="flex-1 space-y-1 px-3 py-4">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          data-testid={`nav-${item.to.replace("/", "")}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-orange-500/15 text-orange-300 ring-1 ring-orange-500/30"
                : "text-slate-400 hover:bg-slate-800/70 hover:text-slate-100"
            }`
          }
        >
          <item.icon className="h-[18px] w-[18px] shrink-0" />
          <span className="truncate">{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  const { logout } = useAuth();
  return (
    <div className="flex items-center gap-2.5 px-5 py-5 border-b border-slate-800">
      <div className="rounded-lg bg-orange-500 p-2">
        <Truck className="h-5 w-5 text-white" />
      </div>
      <div className="leading-tight">
        <p className="text-sm font-bold text-white">Central do Courier</p>
        <p className="text-[11px] font-semibold uppercase tracking-widest text-orange-400">
          Console Gestor
        </p>
      </div>
      <div className="ml-auto"><SettingsLink testId="settings-btn" size="h-[18px] w-[18px]" /></div>
      <button
        onClick={logout}
        data-testid="logout-top-btn"
        title="Sair"
        className="rounded-md p-2 text-slate-400 transition-colors hover:bg-rose-500/10 hover:text-rose-300"
      >
        <LogOut className="h-[18px] w-[18px]" />
      </button>
    </div>
  );
}

function UserBox() {
  const { user, logout } = useAuth();
  return (
    <div className="border-t border-slate-800 p-4">
      <div className="mb-3 rounded-lg bg-slate-800/60 p-3">
        <p className="truncate text-sm font-semibold text-slate-100">{user?.name}</p>
        <p className="truncate text-xs text-slate-500">{user?.email}</p>
        <div className="mt-2 flex items-center gap-2">
          <span className="rounded bg-orange-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-orange-300">
            {roleLabel(user?.role)}
          </span>
          {user?.company?.name && (
            <span className="truncate text-[11px] text-slate-400">{user.company.name}</span>
          )}
        </div>
      </div>
      <button
        onClick={logout}
        data-testid="logout-btn"
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:border-rose-500/50 hover:bg-rose-500/10 hover:text-rose-300"
      >
        <LogOut className="h-4 w-4" /> Sair
      </button>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { logout } = useAuth();
  const { selectedBaseId } = useBase();
  const location = useLocation();
  const current = NAV.find((n) => n.to === location.pathname);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-800 bg-slate-900 lg:flex">
        <Brand />
        <NavItems />
        <UserBox />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-800 bg-slate-900/95 px-4 py-3 backdrop-blur lg:hidden">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-orange-500 p-1.5">
            <Truck className="h-4 w-4 text-white" />
          </div>
          <span className="text-sm font-bold text-white">Central do Courier</span>
        </div>
        <div className="flex items-center gap-1">
        <BaseSelector testId="base-selector-mobile" className="w-[120px]" />
        <SettingsLink testId="settings-btn-mobile" size="h-5 w-5" />
        <button onClick={logout} data-testid="logout-top-btn-mobile" title="Sair"
          className="rounded-md p-2 text-slate-300 transition-colors hover:bg-rose-500/10 hover:text-rose-300">
          <LogOut className="h-5 w-5" />
        </button>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="menu-btn" className="rounded-md p-2 text-slate-300 hover:bg-slate-800">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 border-slate-800 bg-slate-900 p-0">
            <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
              <span className="text-sm font-bold text-white">Menu</span>
              <button onClick={() => setOpen(false)} className="text-slate-400">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex h-[calc(100%-57px)] flex-col">
              <NavItems onNavigate={() => setOpen(false)} />
              <UserBox />
            </div>
          </SheetContent>
        </Sheet>
        </div>
      </header>

      {/* Content */}
      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
          <div className="mb-6 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {REPORT_PATHS.includes(location.pathname) && <ReportLogo />}
              <h1 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                {current?.label || (location.pathname === "/configuracoes" ? "Configurações" : "")}
              </h1>
            </div>
            <div className="hidden lg:block"><BaseSelector testId="base-selector-desktop" /></div>
          </div>
          <div key={selectedBaseId || "all"}>
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
